/**
 * Labor Audit by Role
 *
 * Operations panel above the Team table on the Toast Labor page. Audits
 * labor by role across a single Day or a whole Week. The data-source
 * toggle is three-way:
 *
 *   - Scheduled  → Toast `shifts`
 *   - Actual     → Toast `time_entries`
 *   - Compare    → both side-by-side so a manager can see forecast vs
 *                  actual at a glance (paired bars, dual-value matrix)
 *
 * Every bar, segment, and matrix cell is clickable — opens a drawer
 * with the underlying roster (who, when, source). Long shifts that
 * cross midnight are split per day in the week aggregate so day totals
 * reflect what actually happened on each calendar day.
 *
 * All charts are inline SVG — no chart libraries.
 */
import React, { useMemo, useState } from 'react';

const ROLE_PALETTE = [
  '#ff6b35', // brand orange
  '#ff8c5a',
  '#2b3a4b', // slate
  '#7d8a99',
  '#ffd1b3',
  '#5b6b7c',
  '#ffa570',
  '#3d4f63',
];

const DAY_LABELS_SHORT = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const DAY_LABELS_SINGLE = ['S', 'M', 'T', 'W', 'T', 'F', 'S'];

const HOUR_BINS = 48; // half-hour slots, 0..47
const TIMELINE_HOUR_TICKS = [0, 6, 9, 12, 15, 18, 21, 24];

// ─── Date helpers ──────────────────────────────────────────────────

function startOfDay(date) {
  const d = new Date(date);
  d.setHours(0, 0, 0, 0);
  return d;
}

function startOfWeek(date) {
  const d = startOfDay(date);
  d.setDate(d.getDate() - d.getDay());
  return d;
}

function addDays(date, n) {
  const d = new Date(date);
  d.setDate(d.getDate() + n);
  return d;
}

function isSameDay(a, b) {
  return a.toDateString() === b.toDateString();
}

function formatDayHeading(date) {
  return date.toLocaleDateString('en-US', {
    weekday: 'long',
    month: 'short',
    day: 'numeric',
  });
}

function formatWeekHeading(start) {
  const end = addDays(start, 6);
  const sm = start.toLocaleDateString('en-US', { month: 'short' });
  const em = end.toLocaleDateString('en-US', { month: 'short' });
  if (sm === em) return `${sm} ${start.getDate()} – ${end.getDate()}`;
  return `${sm} ${start.getDate()} – ${em} ${end.getDate()}`;
}

function formatHours(h) {
  if (!Number.isFinite(h) || h === 0) return '0';
  if (h < 10) return h.toFixed(1);
  return String(Math.round(h));
}

function formatTime(ms) {
  if (!Number.isFinite(ms)) return '—';
  return new Date(ms).toLocaleTimeString('en-US', {
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  });
}

function fullName(emp) {
  if (!emp) return 'Unknown';
  return `${emp.firstName || ''} ${emp.lastName || ''}`.trim() || 'Unknown';
}

// Render a half-hour slot index (0..47) as a "3:00p – 3:30p" label.
function slotTimeLabel(slot) {
  const fmt = (totalMinutes) => {
    const h24 = Math.floor(totalMinutes / 60) % 24;
    const m = totalMinutes % 60;
    const ampm = h24 < 12 ? 'a' : 'p';
    const h12 = h24 === 0 ? 12 : h24 > 12 ? h24 - 12 : h24;
    return `${h12}:${m.toString().padStart(2, '0')}${ampm}`;
  };
  const startMin = slot * 30;
  const endMin = (slot + 1) * 30;
  return `${fmt(startMin)} – ${fmt(endMin)}`;
}

// ─── Aggregation ────────────────────────────────────────────────────

function overlapMs(startMs, endMs, winStartMs, winEndMs) {
  const lo = Math.max(startMs, winStartMs);
  const hi = Math.min(endMs, winEndMs);
  return Math.max(0, hi - lo);
}

function buildDayAggregate(entries, jobs, dayStart) {
  const dayEnd = addDays(dayStart, 1);
  const dayStartMs = dayStart.getTime();
  const dayEndMs = dayEnd.getTime();
  const slotMs = (24 * 60 * 60 * 1000) / HOUR_BINS;
  const nowMs = Date.now();

  const byRole = new Map();
  let totalMs = 0;
  let totalEntries = 0;
  const distinctStaff = new Set();
  let peakOverall = 0;
  let peakSlot = 0;

  const ensureRole = (guid) => {
    if (!byRole.has(guid)) {
      const job = jobs.find(j => j.guid === guid);
      byRole.set(guid, {
        guid,
        name: job?.title || job?.name || (guid && guid !== '__none__' ? 'Unknown' : 'Unassigned'),
        hours: 0,
        entries: 0,
        people: new Set(),
        slots: new Array(HOUR_BINS).fill(0),
        roster: [],
      });
    }
    return byRole.get(guid);
  };

  for (const entry of entries) {
    if (!entry.inDate) continue;
    const start = new Date(entry.inDate).getTime();
    const end = entry.outDate ? new Date(entry.outDate).getTime() : nowMs;
    if (!Number.isFinite(start) || !Number.isFinite(end) || end <= start) continue;
    const ovMs = overlapMs(start, end, dayStartMs, dayEndMs);
    if (ovMs <= 0) continue;

    const jobGuid = entry.jobReference?.guid || '__none__';
    const empGuid = entry.employeeReference?.guid;
    const role = ensureRole(jobGuid);

    const hrs = ovMs / (1000 * 60 * 60);
    role.hours += hrs;
    role.entries += 1;
    if (empGuid) {
      role.people.add(empGuid);
      distinctStaff.add(empGuid);
    }
    role.roster.push({
      empGuid,
      start: Math.max(start, dayStartMs),
      end: Math.min(end, dayEndMs),
      hours: hrs,
      active: !entry.outDate && nowMs < dayEndMs,
    });

    totalMs += ovMs;
    totalEntries += 1;

    const sIdx = Math.max(0, Math.floor((Math.max(start, dayStartMs) - dayStartMs) / slotMs));
    const eIdx = Math.min(HOUR_BINS, Math.ceil((Math.min(end, dayEndMs) - dayStartMs) / slotMs));
    for (let i = sIdx; i < eIdx; i++) role.slots[i] += 1;
  }

  const overallSlots = new Array(HOUR_BINS).fill(0);
  for (const role of byRole.values()) {
    for (let i = 0; i < HOUR_BINS; i++) {
      overallSlots[i] += role.slots[i];
      if (overallSlots[i] > peakOverall) {
        peakOverall = overallSlots[i];
        peakSlot = i;
      }
    }
  }

  // Sort roster entries by start so drawer renders chronologically.
  for (const role of byRole.values()) {
    role.roster.sort((a, b) => a.start - b.start);
  }

  const roles = Array.from(byRole.values())
    .sort((a, b) => b.hours - a.hours)
    .map((r, i) => ({
      ...r,
      peopleCount: r.people.size,
      color: ROLE_PALETTE[i % ROLE_PALETTE.length],
    }));

  return {
    roles,
    totalHours: totalMs / (1000 * 60 * 60),
    totalEntries,
    distinctStaffCount: distinctStaff.size,
    overallSlots,
    peakOverall,
    peakSlot,
  };
}

function buildWeekAggregate(entries, jobs, weekStart) {
  const weekEnd = addDays(weekStart, 7);
  const weekStartMs = weekStart.getTime();
  const weekEndMs = weekEnd.getTime();
  const dayMs = 24 * 60 * 60 * 1000;
  const nowMs = Date.now();

  const byRole = new Map();
  const dayTotals = new Array(7).fill(0);
  let totalMs = 0;
  let totalEntries = 0;
  const distinctStaff = new Set();

  const ensureRole = (guid) => {
    if (!byRole.has(guid)) {
      const job = jobs.find(j => j.guid === guid);
      byRole.set(guid, {
        guid,
        name: job?.title || job?.name || (guid && guid !== '__none__' ? 'Unknown' : 'Unassigned'),
        hoursByDay: new Array(7).fill(0),
        // Per-(role, day) roster — populated as we split shifts across
        // calendar days. Lets a click on any week-view bar/cell resolve
        // back to the exact people involved.
        rosterByDay: Array.from({ length: 7 }, () => []),
        entries: 0,
        people: new Set(),
      });
    }
    return byRole.get(guid);
  };

  for (const entry of entries) {
    if (!entry.inDate) continue;
    const start = new Date(entry.inDate).getTime();
    const end = entry.outDate ? new Date(entry.outDate).getTime() : nowMs;
    if (!Number.isFinite(start) || !Number.isFinite(end) || end <= start) continue;
    if (end <= weekStartMs || start >= weekEndMs) continue;

    const jobGuid = entry.jobReference?.guid || '__none__';
    const empGuid = entry.employeeReference?.guid;
    const role = ensureRole(jobGuid);

    for (let d = 0; d < 7; d++) {
      const dayStartMs = weekStartMs + d * dayMs;
      const dayEndMs = dayStartMs + dayMs;
      const ovMs = overlapMs(start, end, dayStartMs, dayEndMs);
      if (ovMs > 0) {
        const hrs = ovMs / (1000 * 60 * 60);
        role.hoursByDay[d] += hrs;
        dayTotals[d] += hrs;
        totalMs += ovMs;
        role.rosterByDay[d].push({
          empGuid,
          start: Math.max(start, dayStartMs),
          end: Math.min(end, dayEndMs),
          hours: hrs,
          active: !entry.outDate && nowMs < dayEndMs,
        });
      }
    }
    role.entries += 1;
    if (empGuid) {
      role.people.add(empGuid);
      distinctStaff.add(empGuid);
    }
    totalEntries += 1;
  }

  // Chronological order within each (role, day) roster.
  for (const role of byRole.values()) {
    for (let d = 0; d < 7; d++) {
      role.rosterByDay[d].sort((a, b) => a.start - b.start);
    }
  }

  const roles = Array.from(byRole.values())
    .map(r => ({
      ...r,
      totalHours: r.hoursByDay.reduce((a, b) => a + b, 0),
      peopleCount: r.people.size,
    }))
    .sort((a, b) => b.totalHours - a.totalHours)
    .map((r, i) => ({ ...r, color: ROLE_PALETTE[i % ROLE_PALETTE.length] }));

  let peakDay = 0;
  for (let d = 1; d < 7; d++) if (dayTotals[d] > dayTotals[peakDay]) peakDay = d;

  return {
    roles,
    dayTotals,
    totalHours: totalMs / (1000 * 60 * 60),
    totalEntries,
    distinctStaffCount: distinctStaff.size,
    peakDay,
    peakDayHours: dayTotals[peakDay],
  };
}

/**
 * Unify two single-mode aggregates by role guid so paired rendering
 * (compare mode) lines up. Roles are sorted by max(schedHours, actualHours)
 * so the visually dominant role anchors the top of every list.
 *
 * `colorFromAgg` re-uses whichever agg already mapped a color for the
 * role — keeps the palette stable across modes.
 */
function mergeRolesByGuid(schedAgg, actualAgg, picker) {
  const map = new Map();
  for (const r of schedAgg.roles) {
    map.set(r.guid, {
      guid: r.guid,
      name: r.name,
      color: r.color,
      sched: picker(r, 'sched'),
      actual: null,
    });
  }
  for (const r of actualAgg.roles) {
    if (!map.has(r.guid)) {
      map.set(r.guid, {
        guid: r.guid,
        name: r.name,
        color: r.color,
        sched: null,
        actual: picker(r, 'actual'),
      });
    } else {
      map.get(r.guid).actual = picker(r, 'actual');
    }
  }
  return Array.from(map.values()).sort((a, b) => {
    const aMax = Math.max(a.sched?.sortKey || 0, a.actual?.sortKey || 0);
    const bMax = Math.max(b.sched?.sortKey || 0, b.actual?.sortKey || 0);
    return bMax - aMax;
  }).map((r, i) => ({
    ...r,
    // Re-assign palette after merge so largest gets brand orange.
    color: ROLE_PALETTE[i % ROLE_PALETTE.length],
  }));
}

// ─── SVG: Day swimlane timeline ─────────────────────────────────────

const SwimlaneTimeline = ({
  roles,
  overallSlots,
  peakOverall,
  source,
  onSelectRole,
  onSelectSlot,
  dayStart,
}) => {
  const padLeft = 88;
  const padRight = 16;
  const padTop = 28;
  const rowH = 22;
  const rowGap = 4;
  const overallH = 28;
  const vbW = 720;
  const slotsW = vbW - padLeft - padRight;
  const slotW = slotsW / HOUR_BINS;
  const height = padTop + overallH + 6 + Math.max(1, roles.length) * (rowH + rowGap);

  const overallMax = Math.max(1, peakOverall);

  return (
    <svg
      viewBox={`0 0 ${vbW} ${height}`}
      width="100%"
      preserveAspectRatio="xMidYMid meet"
      role="img"
      aria-label={`Hourly coverage by role — ${source}`}
      className="audit-svg"
    >
      {TIMELINE_HOUR_TICKS.map((h, i) => {
        const x = padLeft + (h / 24) * slotsW;
        const label = h === 0 ? '12a' : h === 24 ? '12a' : h === 12 ? '12p' : h < 12 ? `${h}a` : `${h - 12}p`;
        return (
          <g key={i}>
            <line
              x1={x}
              x2={x}
              y1={padTop - 4}
              y2={height - 4}
              stroke="#eee"
              strokeWidth="1"
              strokeDasharray={h === 0 || h === 24 ? null : '2 3'}
            />
            <text
              x={x}
              y={padTop - 10}
              textAnchor="middle"
              fontSize="9"
              fill="#9a9a9a"
              fontFamily="ui-monospace, SFMono-Regular, Menlo, monospace"
              letterSpacing="0.05em"
            >
              {label}
            </text>
          </g>
        );
      })}

      <g>
        <text
          x={padLeft - 10}
          y={padTop + overallH / 2 + 3}
          textAnchor="end"
          fontSize="9"
          fill="#999"
          letterSpacing="0.1em"
          fontWeight="700"
        >
          ALL
        </text>
        {overallSlots.map((v, i) => {
          if (v === 0) return null;
          const x = padLeft + i * slotW;
          const h = (v / overallMax) * overallH;
          return (
            <rect
              key={i}
              className={onSelectSlot ? 'audit-svg-clickable' : ''}
              x={x}
              y={padTop + overallH - h}
              width={slotW - 0.6}
              height={h}
              fill="#1a1a1a"
              fillOpacity="0.78"
              onClick={onSelectSlot ? (e) => {
                e.stopPropagation();
                onSelectSlot({ slot: i, roleGuid: null });
              } : undefined}
            >
              {onSelectSlot && (
                <title>{`${slotTimeLabel(i)} — ${v} on (all roles)`}</title>
              )}
            </rect>
          );
        })}
      </g>

      {roles.map((r, ri) => {
        const y = padTop + overallH + 6 + ri * (rowH + rowGap);
        const roleMax = Math.max(1, ...r.slots);
        return (
          <g
            key={r.guid}
            className="audit-svg-clickable"
            onClick={onSelectRole ? () => onSelectRole(r) : undefined}
          >
            <rect x={padLeft} y={y} width={slotsW} height={rowH} fill="#fafafa" />
            <text
              x={padLeft - 10}
              y={y + rowH / 2 + 3}
              textAnchor="end"
              fontSize="10.5"
              fill="#1a1a1a"
              fontWeight="600"
              letterSpacing="0.01em"
            >
              {r.name.length > 12 ? r.name.slice(0, 12) + '…' : r.name}
            </text>
            <rect
              x={padLeft - 5}
              y={y}
              width="3"
              height={rowH}
              fill={r.color}
              rx="1"
            />
            {r.slots.map((v, i) => {
              if (v === 0) return null;
              const x = padLeft + i * slotW;
              const opacity = 0.25 + 0.75 * Math.min(1, v / roleMax);
              return (
                <rect
                  key={i}
                  className={onSelectSlot ? 'audit-svg-clickable' : ''}
                  x={x}
                  y={y + 1}
                  width={slotW - 0.4}
                  height={rowH - 2}
                  fill={r.color}
                  fillOpacity={opacity}
                  onClick={onSelectSlot ? (e) => {
                    e.stopPropagation();
                    onSelectSlot({ slot: i, roleGuid: r.guid, role: r });
                  } : undefined}
                >
                  {onSelectSlot && (
                    <title>{`${r.name} · ${slotTimeLabel(i)} — ${v} on`}</title>
                  )}
                </rect>
              );
            })}
            <text
              x={padLeft + slotsW + 6}
              y={y + rowH / 2 + 3}
              textAnchor="start"
              fontSize="10"
              fill="#666"
              fontFamily="ui-monospace, SFMono-Regular, Menlo, monospace"
            >
              {Math.max(...r.slots)}
            </text>
          </g>
        );
      })}
    </svg>
  );
};

// ─── SVG: Week stacked daily bars ───────────────────────────────────

/**
 * Vertical bars per day, segmented by role. In single mode there's one
 * bar per day; in compare mode each day shows two narrower bars side by
 * side (scheduled left, actual right) so eye-level comparison is
 * trivial. Every segment is independently clickable — clicking a slice
 * surfaces the people in that role on that day, from the matching
 * source aggregate.
 */
const StackedDailyBars = ({
  schedAgg,
  actualAgg,
  weekStart,
  mode,
  onSelect,
}) => {
  const compare = mode === 'compare';
  const padLeft = 36;
  const padRight = 16;
  const padTop = 18;
  const padBottom = 44;
  const height = 240;
  const vbW = 580;
  const chartW = vbW - padLeft - padRight;
  const chartH = height - padTop - padBottom;
  const colCount = 7;
  const colSlot = chartW / colCount;

  // In compare mode, two bars per day; otherwise one centered.
  const barW = compare ? Math.min(28, colSlot * 0.38) : Math.min(48, colSlot * 0.66);
  const barGap = compare ? 4 : 0;

  // Pick the displayed agg per mode + max across the chart so both bars
  // share a single y-axis. Compare mode needs the max from both aggs.
  const primaryDayTotals = mode === 'actual' ? actualAgg.dayTotals : schedAgg.dayTotals;
  const compareDayMax = Math.max(
    1,
    ...schedAgg.dayTotals,
    ...actualAgg.dayTotals,
  );
  const singleDayMax = Math.max(1, ...primaryDayTotals);
  const max = compare ? compareDayMax : singleDayMax;

  const todayIdx = (() => {
    const today = startOfDay(new Date());
    for (let i = 0; i < 7; i++) {
      if (isSameDay(addDays(weekStart, i), today)) return i;
    }
    return -1;
  })();

  // For a given source agg + day index, return ordered slices for the
  // stacked bar (largest first so the dominant role anchors the base).
  const slicesFor = (agg, d) => agg.roles
    .map(r => ({
      guid: r.guid,
      name: r.name,
      color: r.color,
      hrs: r.hoursByDay[d] || 0,
      roster: r.rosterByDay[d] || [],
    }))
    .filter(s => s.hrs > 0)
    .sort((a, b) => b.hrs - a.hrs);

  const drawBar = (xBar, slices, dayHours, source, dayIdx) => {
    let yCursor = padTop + chartH;
    const totalH = (dayHours / max) * chartH;
    return (
      <g key={`${source}-${dayIdx}`}>
        {slices.map((s, ri) => {
          const segH = (s.hrs / max) * chartH;
          yCursor -= segH;
          const isDashed = source === 'sched' && compare;
          return (
            <rect
              key={ri}
              className="audit-svg-clickable"
              x={xBar}
              y={yCursor}
              width={barW}
              height={Math.max(0.5, segH - 0.6)}
              fill={s.color}
              fillOpacity={isDashed ? 0.55 : 1}
              stroke={isDashed ? s.color : 'none'}
              strokeWidth={isDashed ? 0.8 : 0}
              strokeDasharray={isDashed ? '2 2' : null}
              rx="1"
              onClick={onSelect ? () => onSelect({
                source,
                roleGuid: s.guid,
                roleName: s.name,
                roleColor: s.color,
                dayIdx,
                roster: s.roster,
              }) : undefined}
            >
              <title>
                {s.name} · {formatHours(s.hrs)}h · {source === 'sched' ? 'Scheduled' : 'Actual'}
              </title>
            </rect>
          );
        })}
        {dayHours === 0 && (
          <line
            x1={xBar}
            x2={xBar + barW}
            y1={padTop + chartH}
            y2={padTop + chartH}
            stroke="#ddd"
            strokeWidth="1"
          />
        )}
        {dayHours > 0 && (
          <text
            x={xBar + barW / 2}
            y={padTop + chartH - totalH - 4}
            textAnchor="middle"
            fontSize="9.5"
            fontWeight="700"
            fill={source === 'sched' && compare ? '#7a4a2a' : '#1a1a1a'}
            fontFamily="ui-monospace, SFMono-Regular, Menlo, monospace"
          >
            {formatHours(dayHours)}
          </text>
        )}
      </g>
    );
  };

  const yTicks = [0, 0.5, 1];

  return (
    <svg
      viewBox={`0 0 ${vbW} ${height}`}
      width="100%"
      preserveAspectRatio="xMidYMid meet"
      role="img"
      aria-label="Hours by day, stacked by role"
      className="audit-svg"
    >
      {yTicks.map((t, i) => {
        const y = padTop + chartH * (1 - t);
        return (
          <g key={i}>
            <line x1={padLeft} x2={vbW - padRight} y1={y} y2={y} stroke="#eee" strokeWidth="1" />
            <text
              x={padLeft - 6}
              y={y + 3}
              textAnchor="end"
              fontSize="9"
              fill="#9a9a9a"
              fontFamily="ui-monospace, SFMono-Regular, Menlo, monospace"
            >
              {Math.round(max * t)}
            </text>
          </g>
        );
      })}

      {Array.from({ length: colCount }, (_, d) => {
        const colCenter = padLeft + d * colSlot + colSlot / 2;
        const date = addDays(weekStart, d);
        const isToday = d === todayIdx;
        const schedSlices = slicesFor(schedAgg, d);
        const actualSlices = slicesFor(actualAgg, d);
        const schedHrs = schedAgg.dayTotals[d] || 0;
        const actualHrs = actualAgg.dayTotals[d] || 0;

        // Bar positions
        let leftBarX, rightBarX;
        if (compare) {
          leftBarX = colCenter - barW - barGap / 2;
          rightBarX = colCenter + barGap / 2;
        } else {
          leftBarX = colCenter - barW / 2;
          rightBarX = null;
        }

        const primarySlices = mode === 'actual' ? actualSlices : schedSlices;
        const primaryHrs = mode === 'actual' ? actualHrs : schedHrs;

        return (
          <g key={d}>
            {isToday && (
              <rect
                x={colCenter - colSlot * 0.45}
                y={padTop - 4}
                width={colSlot * 0.9}
                height={chartH + 16}
                fill="#fff4ec"
                rx="6"
              />
            )}

            {compare ? (
              <>
                {drawBar(leftBarX, schedSlices, schedHrs, 'sched', d)}
                {drawBar(rightBarX, actualSlices, actualHrs, 'actual', d)}
              </>
            ) : (
              drawBar(leftBarX, primarySlices, primaryHrs, mode === 'actual' ? 'actual' : 'sched', d)
            )}

            <text
              x={colCenter}
              y={padTop + chartH + 16}
              textAnchor="middle"
              fontSize="10"
              fontWeight={isToday ? 700 : 600}
              fill={isToday ? '#ff6b35' : '#666'}
              letterSpacing="0.06em"
            >
              {DAY_LABELS_SINGLE[d]}
            </text>
            <text
              x={colCenter}
              y={padTop + chartH + 28}
              textAnchor="middle"
              fontSize="9"
              fill={isToday ? '#ff6b35' : '#9a9a9a'}
              fontFamily="ui-monospace, SFMono-Regular, Menlo, monospace"
            >
              {date.getDate()}
            </text>
            {compare && (
              <text
                x={colCenter}
                y={padTop + chartH + 40}
                textAnchor="middle"
                fontSize="8.5"
                fill="#9a9a9a"
                letterSpacing="0.06em"
              >
                S · A
              </text>
            )}
          </g>
        );
      })}
    </svg>
  );
};

// ─── Main component ────────────────────────────────────────────────

const ToastLaborAuditView = ({
  shifts = [],
  timeEntries = [],
  employees = [],
  jobs = [],
  shiftsLoading = false,
  timeEntriesLoading = false,
}) => {
  const [tab, setTab] = useState('day');
  // 'scheduled' | 'actual' | 'compare'
  const [mode, setMode] = useState('scheduled');
  const [dayOffset, setDayOffset] = useState(0);
  const [weekOffset, setWeekOffset] = useState(0);
  // Drill-down selection: { source, tab, roleGuid, roleName, roleColor,
  // periodLabel, roster }. Null when the drawer is closed.
  const [selection, setSelection] = useState(null);

  const employeeIndex = useMemo(
    () => Object.fromEntries(employees.map(e => [e.guid, e])),
    [employees]
  );

  const today = startOfDay(new Date());
  const selectedDay = addDays(today, dayOffset);
  const selectedWeekStart = addDays(startOfWeek(today), weekOffset * 7);

  // Build BOTH aggregates always — needed for compare mode and for the
  // drill-down drawer's source label. Cheap relative to fetch latency.
  const dayAggSched = useMemo(
    () => buildDayAggregate(shifts, jobs, selectedDay),
    [shifts, jobs, selectedDay.getTime()]
  );
  const dayAggActual = useMemo(
    () => buildDayAggregate(timeEntries, jobs, selectedDay),
    [timeEntries, jobs, selectedDay.getTime()]
  );
  const weekAggSched = useMemo(
    () => buildWeekAggregate(shifts, jobs, selectedWeekStart),
    [shifts, jobs, selectedWeekStart.getTime()]
  );
  const weekAggActual = useMemo(
    () => buildWeekAggregate(timeEntries, jobs, selectedWeekStart),
    [timeEntries, jobs, selectedWeekStart.getTime()]
  );

  const sourceLoading = mode === 'actual' ? timeEntriesLoading
    : mode === 'compare' ? (shiftsLoading || timeEntriesLoading)
    : shiftsLoading;
  const sourceHasAny = mode === 'actual' ? timeEntries.length > 0
    : mode === 'compare' ? (shifts.length > 0 || timeEntries.length > 0)
    : shifts.length > 0;

  const isTodaySelected = dayOffset === 0;
  const isThisWeekSelected = weekOffset === 0;

  const openSelection = (payload) => setSelection(payload);
  const closeSelection = () => setSelection(null);

  return (
    <section className="labor-audit" aria-label="Labor audit by role">
      <header className="labor-audit-head">
        <div className="labor-audit-titling">
          <span className="labor-audit-eyebrow">Labor audit</span>
          <h2 className="labor-audit-title">
            By role
            <span className="labor-audit-title-sep" aria-hidden="true">·</span>
            <span className="labor-audit-title-context">
              {tab === 'day' ? formatDayHeading(selectedDay) : `Week of ${formatWeekHeading(selectedWeekStart)}`}
            </span>
          </h2>
        </div>

        <div className="labor-audit-controls">
          <div className="labor-audit-tabs" role="tablist" aria-label="View granularity">
            <button
              role="tab"
              aria-selected={tab === 'day'}
              className={`labor-audit-tab ${tab === 'day' ? 'is-active' : ''}`}
              onClick={() => { setTab('day'); closeSelection(); }}
            >
              Day
            </button>
            <button
              role="tab"
              aria-selected={tab === 'week'}
              className={`labor-audit-tab ${tab === 'week' ? 'is-active' : ''}`}
              onClick={() => { setTab('week'); closeSelection(); }}
            >
              Week
            </button>
          </div>

          <div className="labor-audit-toggle" role="group" aria-label="Data source">
            <button
              type="button"
              className={`labor-audit-toggle-btn ${mode === 'scheduled' ? 'is-active' : ''}`}
              onClick={() => { setMode('scheduled'); closeSelection(); }}
              title="Toast scheduled shifts"
            >
              Scheduled
            </button>
            <button
              type="button"
              className={`labor-audit-toggle-btn ${mode === 'actual' ? 'is-active' : ''}`}
              onClick={() => { setMode('actual'); closeSelection(); }}
              title="Toast time entries (actual worked)"
            >
              Actual
            </button>
            <button
              type="button"
              className={`labor-audit-toggle-btn ${mode === 'compare' ? 'is-active' : ''}`}
              onClick={() => { setMode('compare'); closeSelection(); }}
              title="Forecast vs actual, side-by-side"
            >
              Compare
            </button>
          </div>

          {tab === 'day' ? (
            <div className="labor-audit-nav">
              <button className="labor-audit-nav-btn" onClick={() => setDayOffset(d => d - 1)} aria-label="Previous day">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                  <path d="M15 18l-6-6 6-6" />
                </svg>
              </button>
              <span className="labor-audit-nav-label">
                {isTodaySelected ? 'Today' : selectedDay.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
              </span>
              <button className="labor-audit-nav-btn" onClick={() => setDayOffset(d => d + 1)} aria-label="Next day">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                  <path d="M9 18l6-6-6-6" />
                </svg>
              </button>
              {!isTodaySelected && (
                <button className="labor-audit-today-btn" onClick={() => setDayOffset(0)}>Today</button>
              )}
            </div>
          ) : (
            <div className="labor-audit-nav">
              <button className="labor-audit-nav-btn" onClick={() => setWeekOffset(w => w - 1)} aria-label="Previous week">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                  <path d="M15 18l-6-6 6-6" />
                </svg>
              </button>
              <span className="labor-audit-nav-label">
                {isThisWeekSelected ? 'This week' : formatWeekHeading(selectedWeekStart)}
              </span>
              <button className="labor-audit-nav-btn" onClick={() => setWeekOffset(w => w + 1)} aria-label="Next week">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                  <path d="M9 18l6-6-6-6" />
                </svg>
              </button>
              {!isThisWeekSelected && (
                <button className="labor-audit-today-btn" onClick={() => setWeekOffset(0)}>This week</button>
              )}
            </div>
          )}
        </div>
      </header>

      <hr className="labor-audit-rule" aria-hidden="true" />

      {sourceLoading && !sourceHasAny ? (
        <div className="labor-audit-loading">
          <div className="labor-audit-spinner" aria-hidden="true" />
          <span>
            Loading {mode === 'scheduled' ? 'scheduled shifts'
              : mode === 'actual' ? 'time entries'
              : 'scheduled and actual data'} from Toast…
          </span>
        </div>
      ) : tab === 'day' ? (
        <DayPanel
          dayAggSched={dayAggSched}
          dayAggActual={dayAggActual}
          mode={mode}
          selectedDay={selectedDay}
          onSelect={openSelection}
        />
      ) : (
        <WeekPanel
          weekAggSched={weekAggSched}
          weekAggActual={weekAggActual}
          mode={mode}
          weekStart={selectedWeekStart}
          onSelect={openSelection}
        />
      )}

      {selection && (
        <RosterDrawer
          selection={selection}
          employeeIndex={employeeIndex}
          jobs={jobs}
          shifts={shifts}
          timeEntries={timeEntries}
          weekStart={selectedWeekStart}
          selectedDay={selectedDay}
          tab={tab}
          onClose={closeSelection}
        />
      )}
    </section>
  );
};

// ─── Day panel ──────────────────────────────────────────────────────

const DayPanel = ({
  dayAggSched,
  dayAggActual,
  mode,
  selectedDay,
  onSelect,
}) => {
  const compare = mode === 'compare';
  const primary = mode === 'actual' ? dayAggActual : dayAggSched;

  const peakHourLabel = (() => {
    if (primary.peakOverall === 0) return '—';
    const slot = primary.peakSlot;
    const hour = Math.floor(slot / 2);
    const half = slot % 2 === 0 ? '00' : '30';
    const h12 = hour === 0 ? 12 : hour > 12 ? hour - 12 : hour;
    const ampm = hour < 12 ? 'a' : 'p';
    return `${h12}:${half}${ampm}`;
  })();

  // Build the role rollup rows. In compare mode the rows are merged by
  // role guid so paired bars align; sched/actual rosters are kept
  // separately so a click resolves cleanly to one source.
  const rollupRows = useMemo(() => {
    if (!compare) {
      const agg = primary;
      const maxBar = Math.max(1, ...agg.roles.map(r => r.hours));
      return agg.roles.map(r => ({
        guid: r.guid,
        name: r.name,
        color: r.color,
        maxBar,
        sched: mode === 'scheduled' ? {
          hours: r.hours, people: r.peopleCount, entries: r.entries, roster: r.roster,
        } : null,
        actual: mode === 'actual' ? {
          hours: r.hours, people: r.peopleCount, entries: r.entries, roster: r.roster,
        } : null,
      }));
    }
    // Compare: merge by guid, share a single max scale.
    const map = new Map();
    for (const r of dayAggSched.roles) {
      map.set(r.guid, {
        guid: r.guid, name: r.name, color: r.color,
        sched: { hours: r.hours, people: r.peopleCount, entries: r.entries, roster: r.roster },
        actual: null,
      });
    }
    for (const r of dayAggActual.roles) {
      if (!map.has(r.guid)) {
        map.set(r.guid, {
          guid: r.guid, name: r.name, color: r.color,
          sched: null,
          actual: { hours: r.hours, people: r.peopleCount, entries: r.entries, roster: r.roster },
        });
      } else {
        map.get(r.guid).actual = { hours: r.hours, people: r.peopleCount, entries: r.entries, roster: r.roster };
      }
    }
    const rows = Array.from(map.values()).sort((a, b) => {
      const aMax = Math.max(a.sched?.hours || 0, a.actual?.hours || 0);
      const bMax = Math.max(b.sched?.hours || 0, b.actual?.hours || 0);
      return bMax - aMax;
    }).map((r, i) => ({ ...r, color: ROLE_PALETTE[i % ROLE_PALETTE.length] }));
    const maxBar = Math.max(1, ...rows.map(r => Math.max(r.sched?.hours || 0, r.actual?.hours || 0)));
    return rows.map(r => ({ ...r, maxBar }));
  }, [compare, mode, primary, dayAggSched, dayAggActual]);

  const noData = compare
    ? (dayAggSched.roles.length === 0 && dayAggActual.roles.length === 0)
    : primary.roles.length === 0;

  const kpiItems = compare ? [
    { value: formatHours(dayAggSched.totalHours), unit: 'hrs', label: 'Scheduled' },
    { value: formatHours(dayAggActual.totalHours), unit: 'hrs', label: 'Actual' },
    { value: deltaLabel(dayAggActual.totalHours, dayAggSched.totalHours), label: 'Δ', sub: deltaSub(dayAggActual.totalHours, dayAggSched.totalHours) },
    { value: rollupRows.length, label: 'Roles' },
    { value: Math.max(dayAggSched.distinctStaffCount, dayAggActual.distinctStaffCount), label: 'Distinct staff' },
  ] : [
    { value: formatHours(primary.totalHours), unit: 'hrs', label: 'Total' },
    { value: primary.totalEntries, label: mode === 'scheduled' ? 'Shifts' : 'Entries' },
    { value: primary.distinctStaffCount, label: 'Distinct staff' },
    { value: primary.roles.length, label: 'Roles' },
    { value: primary.peakOverall || '—', label: 'Peak crew', sub: primary.peakOverall ? `@ ${peakHourLabel}` : null },
  ];

  return (
    <>
      <KpiStrip items={kpiItems} />

      {noData ? (
        <EmptyState
          title={`No ${mode === 'actual' ? 'time entries' : mode === 'compare' ? 'shifts or time entries' : 'shifts scheduled'} for this day`}
          hint={
            mode === 'compare'
              ? 'Pick a different day.'
              : `Try the ${mode === 'scheduled' ? 'Actual' : 'Scheduled'} toggle or pick another date.`
          }
        />
      ) : (
        <>
          <div className="labor-audit-section-head">
            <span className="labor-audit-section-eyebrow">Role rollup</span>
            <span className="labor-audit-section-sub">
              {rollupRows.length} roles · sorted by hours{compare ? ' · click a bar to inspect the roster' : ' · click to inspect'}
            </span>
          </div>

          <ul className={`labor-audit-rollup ${compare ? 'is-compare' : ''}`} aria-label="Role rollup">
            {rollupRows.map(r => (
              <RollupRow
                key={r.guid}
                row={r}
                compare={compare}
                onSelect={onSelect}
              />
            ))}
          </ul>

          <div className="labor-audit-section-head labor-audit-section-head--spaced">
            <span className="labor-audit-section-eyebrow">Coverage timeline</span>
            <span className="labor-audit-section-sub">
              {compare
                ? 'Scheduled (top) vs actual (bottom) · 30-min resolution'
                : `Concurrent ${mode === 'actual' ? 'clocked-in' : 'scheduled'} staff · 30-min resolution`}
            </span>
          </div>

          {compare ? (
            <>
              <div className="labor-audit-timeline-wrap">
                <div className="labor-audit-timeline-label">Scheduled</div>
                <SwimlaneTimeline
                  roles={dayAggSched.roles}
                  overallSlots={dayAggSched.overallSlots}
                  peakOverall={dayAggSched.peakOverall}
                  source="scheduled"
                  onSelectRole={(role) => onSelect({
                    source: 'sched',
                    roleGuid: role.guid,
                    roleName: role.name,
                    roleColor: role.color,
                    roster: role.roster,
                  })}
                  onSelectSlot={(s) => onSelect(slotSelection(s, dayAggSched, 'sched', selectedDay))}
                />
              </div>
              <div className="labor-audit-timeline-wrap" style={{ marginTop: 10 }}>
                <div className="labor-audit-timeline-label">Actual</div>
                <SwimlaneTimeline
                  roles={dayAggActual.roles}
                  overallSlots={dayAggActual.overallSlots}
                  peakOverall={dayAggActual.peakOverall}
                  source="actual"
                  onSelectRole={(role) => onSelect({
                    source: 'actual',
                    roleGuid: role.guid,
                    roleName: role.name,
                    roleColor: role.color,
                    roster: role.roster,
                  })}
                  onSelectSlot={(s) => onSelect(slotSelection(s, dayAggActual, 'actual', selectedDay))}
                />
              </div>
            </>
          ) : (
            <div className="labor-audit-timeline-wrap">
              <SwimlaneTimeline
                roles={primary.roles}
                overallSlots={primary.overallSlots}
                peakOverall={primary.peakOverall}
                source={mode}
                onSelectRole={(role) => onSelect({
                  source: mode === 'actual' ? 'actual' : 'sched',
                  roleGuid: role.guid,
                  roleName: role.name,
                  roleColor: role.color,
                  roster: role.roster,
                })}
                onSelectSlot={(s) => onSelect(slotSelection(s, primary, mode === 'actual' ? 'actual' : 'sched', selectedDay))}
              />
            </div>
          )}
        </>
      )}
    </>
  );
};

const RollupRow = ({ row, compare, onSelect }) => {
  const handleClick = (source) => {
    const side = source === 'sched' ? row.sched : row.actual;
    if (!side) return;
    onSelect({
      source,
      roleGuid: row.guid,
      roleName: row.name,
      roleColor: row.color,
      roster: side.roster,
    });
  };

  if (!compare) {
    const side = row.sched || row.actual;
    if (!side) return null;
    const pct = (side.hours / row.maxBar) * 100;
    return (
      <li
        className="labor-audit-rollup-row labor-audit-rollup-row--clickable"
        onClick={() => handleClick(row.sched ? 'sched' : 'actual')}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') handleClick(row.sched ? 'sched' : 'actual'); }}
      >
        <span className="labor-audit-rollup-stripe" style={{ background: row.color }} aria-hidden="true" />
        <span className="labor-audit-rollup-name">{row.name}</span>
        <span className="labor-audit-rollup-bar" aria-hidden="true">
          <span
            className="labor-audit-rollup-bar-fill"
            style={{ width: `${pct}%`, background: row.color }}
          />
        </span>
        <span className="labor-audit-rollup-hrs">
          <strong>{formatHours(side.hours)}</strong>
          <span className="u">h</span>
        </span>
        <span className="labor-audit-rollup-meta">
          {side.people} {side.people === 1 ? 'person' : 'people'} · {side.entries} {row.sched ? 'shft' : 'ent'}
        </span>
      </li>
    );
  }

  // Compare row: paired S/A bars stacked vertically. Each is its own
  // click target so the drawer can route to the right source.
  const schedPct = ((row.sched?.hours || 0) / row.maxBar) * 100;
  const actualPct = ((row.actual?.hours || 0) / row.maxBar) * 100;
  return (
    <li className="labor-audit-rollup-row labor-audit-rollup-row--compare">
      <span className="labor-audit-rollup-stripe" style={{ background: row.color }} aria-hidden="true" />
      <span className="labor-audit-rollup-name">{row.name}</span>
      <div className="labor-audit-rollup-pair">
        <button
          type="button"
          className="labor-audit-rollup-pair-row labor-audit-rollup-pair-row--sched"
          onClick={() => row.sched && handleClick('sched')}
          disabled={!row.sched}
          title={row.sched ? 'See scheduled roster' : 'No scheduled data'}
        >
          <span className="labor-audit-rollup-pair-tag">S</span>
          <span className="labor-audit-rollup-bar" aria-hidden="true">
            <span
              className="labor-audit-rollup-bar-fill is-sched"
              style={{ width: `${schedPct}%`, background: row.color }}
            />
          </span>
          <span className="labor-audit-rollup-pair-hrs">
            {row.sched ? <><strong>{formatHours(row.sched.hours)}</strong><span className="u">h</span></> : <span className="u">—</span>}
          </span>
          <span className="labor-audit-rollup-pair-people">
            {row.sched ? `${row.sched.people}` : '—'}
          </span>
        </button>
        <button
          type="button"
          className="labor-audit-rollup-pair-row labor-audit-rollup-pair-row--actual"
          onClick={() => row.actual && handleClick('actual')}
          disabled={!row.actual}
          title={row.actual ? 'See actual roster' : 'No actual data'}
        >
          <span className="labor-audit-rollup-pair-tag">A</span>
          <span className="labor-audit-rollup-bar" aria-hidden="true">
            <span
              className="labor-audit-rollup-bar-fill is-actual"
              style={{ width: `${actualPct}%`, background: row.color }}
            />
          </span>
          <span className="labor-audit-rollup-pair-hrs">
            {row.actual ? <><strong>{formatHours(row.actual.hours)}</strong><span className="u">h</span></> : <span className="u">—</span>}
          </span>
          <span className="labor-audit-rollup-pair-people">
            {row.actual ? `${row.actual.people}` : '—'}
          </span>
        </button>
      </div>
      <span className="labor-audit-rollup-delta">
        {(() => {
          const s = row.sched?.hours || 0;
          const a = row.actual?.hours || 0;
          if (s === 0 && a === 0) return '';
          const diff = a - s;
          const cls = diff > 0 ? 'is-up' : diff < 0 ? 'is-down' : 'is-flat';
          const sign = diff > 0 ? '+' : '';
          return <span className={`labor-audit-delta ${cls}`}>{sign}{formatHours(Math.abs(diff))}h</span>;
        })()}
      </span>
    </li>
  );
};

// ─── Week panel ─────────────────────────────────────────────────────

const WeekPanel = ({
  weekAggSched,
  weekAggActual,
  mode,
  weekStart,
  onSelect,
}) => {
  const compare = mode === 'compare';
  const primary = mode === 'actual' ? weekAggActual : weekAggSched;

  const peakDayLabel = (() => {
    const idx = primary.peakDay;
    return addDays(weekStart, idx).toLocaleDateString('en-US', { weekday: 'short' });
  })();

  const matrixMax = useMemo(() => {
    const all = compare
      ? [
        ...weekAggSched.roles.flatMap(r => r.hoursByDay),
        ...weekAggActual.roles.flatMap(r => r.hoursByDay),
      ]
      : primary.roles.flatMap(r => r.hoursByDay);
    return Math.max(1, ...all);
  }, [compare, weekAggSched, weekAggActual, primary]);

  const noData = compare
    ? (weekAggSched.roles.length === 0 && weekAggActual.roles.length === 0)
    : primary.roles.length === 0;

  const kpiItems = compare ? [
    { value: formatHours(weekAggSched.totalHours), unit: 'hrs', label: 'Scheduled' },
    { value: formatHours(weekAggActual.totalHours), unit: 'hrs', label: 'Actual' },
    { value: deltaLabel(weekAggActual.totalHours, weekAggSched.totalHours), label: 'Δ', sub: deltaSub(weekAggActual.totalHours, weekAggSched.totalHours) },
    { value: Math.max(weekAggSched.roles.length, weekAggActual.roles.length), label: 'Roles' },
    { value: Math.max(weekAggSched.distinctStaffCount, weekAggActual.distinctStaffCount), label: 'Distinct staff' },
  ] : [
    { value: formatHours(primary.totalHours), unit: 'hrs', label: 'Total' },
    { value: primary.totalEntries, label: mode === 'actual' ? 'Entries' : 'Shifts' },
    { value: primary.distinctStaffCount, label: 'Distinct staff' },
    { value: primary.roles.length, label: 'Roles' },
    { value: peakDayLabel, label: 'Peak day', sub: primary.peakDayHours ? `${formatHours(primary.peakDayHours)}h` : null },
  ];

  return (
    <>
      <KpiStrip items={kpiItems} />

      {noData ? (
        <EmptyState
          title={`No ${mode === 'actual' ? 'time entries' : mode === 'compare' ? 'data' : 'shifts scheduled'} for this week`}
          hint={
            mode === 'compare'
              ? 'Pick a different week.'
              : `Try the ${mode === 'scheduled' ? 'Actual' : 'Scheduled'} toggle or pick another week.`
          }
        />
      ) : (
        <div className="labor-audit-week-grid">
          <div className="labor-audit-week-bars">
            <div className="labor-audit-section-head">
              <span className="labor-audit-section-eyebrow">Hours by day, stacked by role</span>
              <span className="labor-audit-section-sub">
                {compare ? 'S = scheduled · A = actual · click a slice to see who' : 'Top role anchors the base · click a slice to see who'}
              </span>
            </div>
            <StackedDailyBars
              schedAgg={weekAggSched}
              actualAgg={weekAggActual}
              weekStart={weekStart}
              mode={mode}
              onSelect={(slice) => onSelect({
                source: slice.source,
                roleGuid: slice.roleGuid,
                roleName: slice.roleName,
                roleColor: slice.roleColor,
                dayIdx: slice.dayIdx,
                roster: slice.roster,
              })}
            />
            <RoleLegend
              roles={compare
                ? mergeWeekLegend(weekAggSched, weekAggActual)
                : primary.roles
              }
              compare={compare}
            />
          </div>

          <div className="labor-audit-week-matrix">
            <div className="labor-audit-section-head">
              <span className="labor-audit-section-eyebrow">Role × day matrix</span>
              <span className="labor-audit-section-sub">
                {compare ? 'Actual / scheduled per role per day · click a cell' : 'Hours per role per day · click a cell'}
              </span>
            </div>
            <RoleByDayMatrix
              schedAgg={weekAggSched}
              actualAgg={weekAggActual}
              mode={mode}
              weekStart={weekStart}
              matrixMax={matrixMax}
              onSelect={onSelect}
            />
          </div>
        </div>
      )}
    </>
  );
};

// Merge role legend entries by guid for compare-mode display, showing
// the larger of (sched, actual) total alongside each role name.
function mergeWeekLegend(schedAgg, actualAgg) {
  const map = new Map();
  for (const r of schedAgg.roles) {
    map.set(r.guid, {
      guid: r.guid,
      name: r.name,
      color: r.color,
      schedHours: r.totalHours,
      actualHours: 0,
    });
  }
  for (const r of actualAgg.roles) {
    if (map.has(r.guid)) {
      map.get(r.guid).actualHours = r.totalHours;
    } else {
      map.set(r.guid, {
        guid: r.guid,
        name: r.name,
        color: r.color,
        schedHours: 0,
        actualHours: r.totalHours,
      });
    }
  }
  return Array.from(map.values())
    .sort((a, b) => Math.max(b.schedHours, b.actualHours) - Math.max(a.schedHours, a.actualHours))
    .map((r, i) => ({ ...r, color: ROLE_PALETTE[i % ROLE_PALETTE.length], totalHours: Math.max(r.schedHours, r.actualHours) }));
}

// ─── Small shared pieces ────────────────────────────────────────────

const KpiStrip = ({ items }) => (
  <div className="labor-audit-kpis">
    {items.map((k, i) => (
      <div key={i} className="labor-audit-kpi">
        <span className="labor-audit-kpi-value">
          <strong>{k.value}</strong>
          {k.unit && <span className="labor-audit-kpi-unit">{k.unit}</span>}
        </span>
        <span className="labor-audit-kpi-label">{k.label}</span>
        {k.sub && <span className="labor-audit-kpi-sub">{k.sub}</span>}
      </div>
    ))}
  </div>
);

const RoleLegend = ({ roles, compare }) => (
  <ul className="labor-audit-legend" aria-label="Role legend">
    {roles.map(r => (
      <li key={r.guid} className="labor-audit-legend-item">
        <span className="labor-audit-legend-swatch" style={{ background: r.color }} aria-hidden="true" />
        <span className="labor-audit-legend-name">{r.name}</span>
        <span className="labor-audit-legend-hrs">
          {compare ? (
            <>
              <span title="Scheduled">S {formatHours(r.schedHours)}</span>
              <span className="labor-audit-legend-sep">·</span>
              <span title="Actual">A {formatHours(r.actualHours)}</span>
            </>
          ) : (
            <>{formatHours(r.totalHours)}<span className="u">h</span></>
          )}
        </span>
      </li>
    ))}
  </ul>
);

const RoleByDayMatrix = ({ schedAgg, actualAgg, mode, weekStart, matrixMax, onSelect }) => {
  const compare = mode === 'compare';
  const primary = mode === 'actual' ? actualAgg : schedAgg;
  const today = startOfDay(new Date());

  // Build the row set. In compare mode it's the merged union; in single
  // mode it's the primary agg's roles.
  const rows = useMemo(() => {
    if (!compare) {
      return primary.roles.map(r => ({
        guid: r.guid, name: r.name, color: r.color,
        primaryHoursByDay: r.hoursByDay,
        primaryRosterByDay: r.rosterByDay,
        primaryTotal: r.totalHours,
        // For sched/actual selection later
        sched: mode === 'scheduled' ? { hoursByDay: r.hoursByDay, rosterByDay: r.rosterByDay, totalHours: r.totalHours } : null,
        actual: mode === 'actual' ? { hoursByDay: r.hoursByDay, rosterByDay: r.rosterByDay, totalHours: r.totalHours } : null,
      }));
    }
    const map = new Map();
    for (const r of schedAgg.roles) {
      map.set(r.guid, {
        guid: r.guid, name: r.name, color: r.color,
        sched: { hoursByDay: r.hoursByDay, rosterByDay: r.rosterByDay, totalHours: r.totalHours },
        actual: null,
      });
    }
    for (const r of actualAgg.roles) {
      if (!map.has(r.guid)) {
        map.set(r.guid, {
          guid: r.guid, name: r.name, color: r.color,
          sched: null,
          actual: { hoursByDay: r.hoursByDay, rosterByDay: r.rosterByDay, totalHours: r.totalHours },
        });
      } else {
        map.get(r.guid).actual = { hoursByDay: r.hoursByDay, rosterByDay: r.rosterByDay, totalHours: r.totalHours };
      }
    }
    return Array.from(map.values())
      .sort((a, b) => {
        const aMax = Math.max(a.sched?.totalHours || 0, a.actual?.totalHours || 0);
        const bMax = Math.max(b.sched?.totalHours || 0, b.actual?.totalHours || 0);
        return bMax - aMax;
      })
      .map((r, i) => ({ ...r, color: ROLE_PALETTE[i % ROLE_PALETTE.length] }));
  }, [compare, mode, primary, schedAgg, actualAgg]);

  const dayTotalsSched = schedAgg.dayTotals;
  const dayTotalsActual = actualAgg.dayTotals;

  return (
    <div className="labor-audit-matrix-wrap">
      <table className={`labor-audit-matrix ${compare ? 'is-compare' : ''}`}>
        <thead>
          <tr>
            <th className="labor-audit-matrix-corner" />
            {DAY_LABELS_SHORT.map((label, d) => {
              const date = addDays(weekStart, d);
              const isToday = isSameDay(date, today);
              return (
                <th key={d} className={`labor-audit-matrix-day ${isToday ? 'is-today' : ''}`}>
                  <span className="labor-audit-matrix-dow">{label}</span>
                  <span className="labor-audit-matrix-dom">{date.getDate()}</span>
                </th>
              );
            })}
            <th className="labor-audit-matrix-total">Total</th>
          </tr>
        </thead>
        <tbody>
          {rows.map(r => (
            <tr key={r.guid}>
              <th scope="row" className="labor-audit-matrix-role">
                <span className="labor-audit-matrix-role-stripe" style={{ background: r.color }} aria-hidden="true" />
                <span className="labor-audit-matrix-role-name">{r.name}</span>
              </th>
              {Array.from({ length: 7 }, (_, d) => {
                if (compare) {
                  const s = r.sched?.hoursByDay[d] || 0;
                  const a = r.actual?.hoursByDay[d] || 0;
                  const intensity = Math.max(s, a) === 0 ? 0 : 0.08 + 0.5 * (Math.max(s, a) / matrixMax);
                  const isEmpty = s === 0 && a === 0;
                  return (
                    <td
                      key={d}
                      className={`labor-audit-matrix-cell labor-audit-matrix-cell--compare ${isEmpty ? 'is-empty' : 'is-clickable'}`}
                      style={{
                        background: isEmpty
                          ? 'transparent'
                          : `color-mix(in srgb, ${r.color} ${Math.round(intensity * 100)}%, #fff)`,
                      }}
                      onClick={() => {
                        if (isEmpty) return;
                        // Click prefers Actual if present; otherwise Sched.
                        const source = a > 0 ? 'actual' : 'sched';
                        const side = source === 'actual' ? r.actual : r.sched;
                        onSelect({
                          source,
                          roleGuid: r.guid,
                          roleName: r.name,
                          roleColor: r.color,
                          dayIdx: d,
                          roster: side?.rosterByDay?.[d] || [],
                        });
                      }}
                    >
                      {isEmpty ? <span className="labor-audit-matrix-dash">·</span> : (
                        <div className="labor-audit-matrix-pair">
                          <span className="labor-audit-matrix-pair-actual" title="Actual">{a === 0 ? '·' : formatHours(a)}</span>
                          <span className="labor-audit-matrix-pair-sep">/</span>
                          <span className="labor-audit-matrix-pair-sched" title="Scheduled">{s === 0 ? '·' : formatHours(s)}</span>
                        </div>
                      )}
                    </td>
                  );
                }
                const h = r.primaryHoursByDay[d] || 0;
                const intensity = h === 0 ? 0 : 0.08 + 0.5 * (h / matrixMax);
                return (
                  <td
                    key={d}
                    className={`labor-audit-matrix-cell ${h === 0 ? 'is-empty' : 'is-clickable'}`}
                    style={{
                      background: h === 0
                        ? 'transparent'
                        : `color-mix(in srgb, ${r.color} ${Math.round(intensity * 100)}%, #fff)`,
                    }}
                    onClick={() => {
                      if (h === 0) return;
                      onSelect({
                        source: mode === 'actual' ? 'actual' : 'sched',
                        roleGuid: r.guid,
                        roleName: r.name,
                        roleColor: r.color,
                        dayIdx: d,
                        roster: r.primaryRosterByDay[d] || [],
                      });
                    }}
                  >
                    {h === 0 ? <span className="labor-audit-matrix-dash">·</span> : formatHours(h)}
                  </td>
                );
              })}
              <td className="labor-audit-matrix-cell labor-audit-matrix-rowtotal">
                {compare ? (
                  <div className="labor-audit-matrix-pair">
                    <span className="labor-audit-matrix-pair-actual">{formatHours(r.actual?.totalHours || 0)}</span>
                    <span className="labor-audit-matrix-pair-sep">/</span>
                    <span className="labor-audit-matrix-pair-sched">{formatHours(r.sched?.totalHours || 0)}</span>
                  </div>
                ) : formatHours(r.primaryTotal)}
              </td>
            </tr>
          ))}
          <tr className="labor-audit-matrix-footrow">
            <th scope="row" className="labor-audit-matrix-role labor-audit-matrix-role--total">Day total</th>
            {Array.from({ length: 7 }, (_, d) => {
              if (compare) {
                const s = dayTotalsSched[d] || 0;
                const a = dayTotalsActual[d] || 0;
                return (
                  <td key={d} className="labor-audit-matrix-cell labor-audit-matrix-coltotal">
                    {s === 0 && a === 0 ? <span className="labor-audit-matrix-dash">·</span> : (
                      <div className="labor-audit-matrix-pair">
                        <span className="labor-audit-matrix-pair-actual">{a === 0 ? '·' : formatHours(a)}</span>
                        <span className="labor-audit-matrix-pair-sep">/</span>
                        <span className="labor-audit-matrix-pair-sched">{s === 0 ? '·' : formatHours(s)}</span>
                      </div>
                    )}
                  </td>
                );
              }
              const h = (mode === 'actual' ? dayTotalsActual : dayTotalsSched)[d] || 0;
              return (
                <td key={d} className="labor-audit-matrix-cell labor-audit-matrix-coltotal">
                  {h === 0 ? <span className="labor-audit-matrix-dash">·</span> : formatHours(h)}
                </td>
              );
            })}
            <td className="labor-audit-matrix-cell labor-audit-matrix-grandtotal">
              {compare ? (
                <div className="labor-audit-matrix-pair">
                  <span className="labor-audit-matrix-pair-actual">{formatHours(actualAgg.totalHours)}</span>
                  <span className="labor-audit-matrix-pair-sep">/</span>
                  <span className="labor-audit-matrix-pair-sched">{formatHours(schedAgg.totalHours)}</span>
                </div>
              ) : formatHours(primary.totalHours)}
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  );
};

const EmptyState = ({ title, hint }) => (
  <div className="labor-audit-empty">
    <span className="labor-audit-empty-glyph" aria-hidden="true">∅</span>
    <h4>{title}</h4>
    <p>{hint}</p>
  </div>
);

// ─── Roster drawer ────────────────────────────────────────────────

/**
 * Drill-down panel: anchored right of the audit section, triggered by
 * clicking any bar / matrix cell / swimlane row. Two views:
 *
 *   - 'roster'   → people behind the clicked bar, with start/end times.
 *                  Each person row is clickable.
 *   - 'employee' → a per-employee audit for the current period (day or
 *                  week) — scheduled vs actual hours, daily breakdown,
 *                  shift list. Has a back button to return to roster.
 *
 * Read-only. No edits.
 */
const RosterDrawer = ({
  selection,
  employeeIndex,
  jobs,
  shifts,
  timeEntries,
  weekStart,
  selectedDay,
  tab,
  onClose,
}) => {
  const { source, roleName, roleColor, dayIdx, roster, windowStart, windowEnd } = selection;
  const sourceLabel = source === 'actual' ? 'Actual' : 'Scheduled';
  const [view, setView] = useState('roster');
  const [focusEmpGuid, setFocusEmpGuid] = useState(null);
  // 'start' = earliest shift first; 'name' = alphabetical; 'hours' = highest first.
  const [sortKey, setSortKey] = useState('start');

  let dateLabel;
  if (tab === 'week' && typeof dayIdx === 'number') {
    dateLabel = addDays(weekStart, dayIdx).toLocaleDateString('en-US', {
      weekday: 'long', month: 'short', day: 'numeric',
    });
  } else {
    dateLabel = formatDayHeading(selectedDay);
  }
  // When the selection carries a half-hour window (swimlane slot click),
  // surface the time range so it's clear the drawer is scoped narrower
  // than the whole day.
  const windowLabel = (Number.isFinite(windowStart) && Number.isFinite(windowEnd))
    ? `${formatTime(windowStart)} – ${formatTime(windowEnd)}`
    : null;

  const byEmployee = useMemo(() => {
    const map = new Map();
    for (const entry of roster || []) {
      const key = entry.empGuid || `__unassigned__${map.size}`;
      if (!map.has(key)) {
        map.set(key, { empGuid: entry.empGuid, segments: [], hours: 0, earliestStart: Infinity });
      }
      const bucket = map.get(key);
      bucket.segments.push(entry);
      bucket.hours += entry.hours || 0;
      if (Number.isFinite(entry.start) && entry.start < bucket.earliestStart) {
        bucket.earliestStart = entry.start;
      }
    }
    const arr = Array.from(map.values());
    // Each person's segments stay chronological so the inline segment
    // list reads top-to-bottom regardless of the row sort.
    for (const p of arr) p.segments.sort((a, b) => a.start - b.start);

    arr.sort((a, b) => {
      if (sortKey === 'name') {
        const an = fullName(employeeIndex[a.empGuid]);
        const bn = fullName(employeeIndex[b.empGuid]);
        return an.localeCompare(bn);
      }
      if (sortKey === 'hours') {
        if (b.hours !== a.hours) return b.hours - a.hours;
        // Stable tiebreak by name so equal-hours rows don't shuffle on re-render.
        return fullName(employeeIndex[a.empGuid]).localeCompare(fullName(employeeIndex[b.empGuid]));
      }
      // 'start' (default): earliest shift first.
      if (a.earliestStart !== b.earliestStart) return a.earliestStart - b.earliestStart;
      return fullName(employeeIndex[a.empGuid]).localeCompare(fullName(employeeIndex[b.empGuid]));
    });
    return arr;
  }, [roster, employeeIndex, sortKey]);

  const totalHours = (roster || []).reduce((acc, r) => acc + (r.hours || 0), 0);

  const openEmployee = (empGuid) => {
    if (!empGuid) return; // unassigned rows aren't drillable
    setFocusEmpGuid(empGuid);
    setView('employee');
  };

  const backToRoster = () => {
    setView('roster');
    setFocusEmpGuid(null);
  };

  return (
    <div
      className="labor-audit-drawer-backdrop"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <aside
        className="labor-audit-drawer"
        role="dialog"
        aria-label={view === 'employee'
          ? `Audit for ${fullName(employeeIndex[focusEmpGuid])}`
          : `Roster for ${roleName} on ${dateLabel} (${sourceLabel})`}
      >
        {view === 'roster' ? (
          <>
            <header className="labor-audit-drawer-head">
              <div className="labor-audit-drawer-head-left">
                <span
                  className="labor-audit-drawer-stripe"
                  style={{ background: roleColor }}
                  aria-hidden="true"
                />
                <div>
                  <div className="labor-audit-drawer-eyebrow">
                    {sourceLabel} · {dateLabel}{windowLabel ? ` · ${windowLabel}` : ''}
                  </div>
                  <h3 className="labor-audit-drawer-title">{roleName}</h3>
                </div>
              </div>
              <button
                type="button"
                className="labor-audit-drawer-close"
                onClick={onClose}
                aria-label="Close roster"
              >
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                  <line x1="6" y1="6" x2="18" y2="18" />
                  <line x1="18" y1="6" x2="6" y2="18" />
                </svg>
              </button>
            </header>

            <div className="labor-audit-drawer-summary">
              <span><strong>{byEmployee.length}</strong> {byEmployee.length === 1 ? 'person' : 'people'}</span>
              <span className="labor-audit-drawer-summary-sep">·</span>
              <span><strong>{formatHours(totalHours)}</strong> hrs</span>
              <span className="labor-audit-drawer-summary-sep">·</span>
              <span><strong>{(roster || []).length}</strong> {(roster || []).length === 1 ? 'entry' : 'entries'}</span>
            </div>

            {byEmployee.length > 1 && (
              <div className="labor-audit-drawer-sort" role="group" aria-label="Sort roster">
                <span className="labor-audit-drawer-sort-label">Sort</span>
                {[
                  { key: 'start', label: 'Start time' },
                  { key: 'name', label: 'Name' },
                  { key: 'hours', label: 'Hours' },
                ].map(opt => (
                  <button
                    key={opt.key}
                    type="button"
                    className={`labor-audit-drawer-sort-btn ${sortKey === opt.key ? 'is-active' : ''}`}
                    onClick={() => setSortKey(opt.key)}
                    aria-pressed={sortKey === opt.key}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
            )}

            {byEmployee.length === 0 ? (
              <div className="labor-audit-drawer-empty">No roster data.</div>
            ) : (
              <ul className="labor-audit-drawer-list">
                {byEmployee.map((person, i) => {
                  const drillable = !!person.empGuid;
                  return (
                    <li key={person.empGuid || i} className="labor-audit-drawer-person">
                      <button
                        type="button"
                        className={`labor-audit-drawer-person-head ${drillable ? 'is-drillable' : ''}`}
                        onClick={() => openEmployee(person.empGuid)}
                        disabled={!drillable}
                        title={drillable ? 'Audit this employee' : 'Unassigned entry — no employee record'}
                      >
                        <span className="labor-audit-drawer-person-name">
                          {fullName(employeeIndex[person.empGuid])}
                        </span>
                        <span className="labor-audit-drawer-person-hours">
                          <strong>{formatHours(person.hours)}</strong>
                          <span className="u">h</span>
                          {drillable && (
                            <svg
                              className="labor-audit-drawer-person-caret"
                              width="12" height="12" viewBox="0 0 24 24"
                              fill="none" stroke="currentColor" strokeWidth="2.5"
                              aria-hidden="true"
                            >
                              <path d="M9 18l6-6-6-6" />
                            </svg>
                          )}
                        </span>
                      </button>
                      <ul className="labor-audit-drawer-segments">
                        {person.segments.map((seg, j) => (
                          <li key={j} className={`labor-audit-drawer-segment ${seg.active ? 'is-active' : ''}`}>
                            <span className="labor-audit-drawer-segment-range">
                              {formatTime(seg.start)} – {seg.active ? <em>now</em> : formatTime(seg.end)}
                            </span>
                            <span className="labor-audit-drawer-segment-hrs">
                              {formatHours(seg.hours)}<span className="u">h</span>
                            </span>
                          </li>
                        ))}
                      </ul>
                    </li>
                  );
                })}
              </ul>
            )}
          </>
        ) : (
          <EmployeeAuditPanel
            empGuid={focusEmpGuid}
            employee={employeeIndex[focusEmpGuid]}
            jobs={jobs}
            shifts={shifts}
            timeEntries={timeEntries}
            tab={tab}
            selectedDay={selectedDay}
            weekStart={weekStart}
            onBack={backToRoster}
            onClose={onClose}
            originRoleColor={roleColor}
          />
        )}
      </aside>
    </div>
  );
};

/**
 * Per-employee audit view inside the drawer. Pulls THIS employee's
 * shifts and time entries from the full lists (not the role-filtered
 * roster), constrained to the current period — Day shows today only;
 * Week shows a per-day breakdown plus a chronological shift list.
 *
 * Surfaces variance front-and-center: scheduled hours, actual hours,
 * delta, and which days they were scheduled but didn't clock in (or
 * clocked in without a scheduled shift).
 */
const EmployeeAuditPanel = ({
  empGuid,
  employee,
  jobs,
  shifts,
  timeEntries,
  tab,
  selectedDay,
  weekStart,
  onBack,
  onClose,
  originRoleColor,
}) => {
  const jobIndex = useMemo(
    () => Object.fromEntries(jobs.map(j => [j.guid, j])),
    [jobs]
  );
  const getRoleName = (guid) => {
    const j = jobIndex[guid];
    return j?.title || j?.name || 'Unknown';
  };

  // Period window — covers both tabs uniformly.
  const periodStart = tab === 'day' ? selectedDay : weekStart;
  const periodEnd = tab === 'day' ? addDays(selectedDay, 1) : addDays(weekStart, 7);
  const periodLabel = tab === 'day'
    ? formatDayHeading(selectedDay)
    : `Week of ${formatWeekHeading(weekStart)}`;

  const audit = useMemo(
    () => buildEmployeeAudit({
      empGuid, shifts, timeEntries, periodStart, periodEnd, tab, getRoleName,
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [empGuid, shifts, timeEntries, periodStart.getTime(), periodEnd.getTime(), tab]
  );

  const deltaSign = audit.delta > 0.05 ? '+' : audit.delta < -0.05 ? '−' : '';
  const deltaClass = audit.delta > 0.05 ? 'is-up'
    : audit.delta < -0.05 ? 'is-down' : 'is-flat';

  return (
    <>
      <header className="labor-audit-drawer-head">
        <div className="labor-audit-drawer-head-left">
          <button
            type="button"
            className="labor-audit-drawer-back"
            onClick={onBack}
            aria-label="Back to roster"
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
              <path d="M15 18l-6-6 6-6" />
            </svg>
          </button>
          <span
            className="labor-audit-drawer-stripe"
            style={{ background: originRoleColor || '#1a1a1a' }}
            aria-hidden="true"
          />
          <div>
            <div className="labor-audit-drawer-eyebrow">
              Employee audit · {periodLabel}
            </div>
            <h3 className="labor-audit-drawer-title">{fullName(employee)}</h3>
          </div>
        </div>
        <button
          type="button"
          className="labor-audit-drawer-close"
          onClick={onClose}
          aria-label="Close drawer"
        >
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
            <line x1="6" y1="6" x2="18" y2="18" />
            <line x1="18" y1="6" x2="6" y2="18" />
          </svg>
        </button>
      </header>

      <div className="labor-audit-emp-kpis">
        <div className="labor-audit-emp-kpi">
          <span className="labor-audit-emp-kpi-value">{formatHours(audit.schedHours)}<span className="u">h</span></span>
          <span className="labor-audit-emp-kpi-label">Scheduled</span>
        </div>
        <div className="labor-audit-emp-kpi">
          <span className="labor-audit-emp-kpi-value">{formatHours(audit.actualHours)}<span className="u">h</span></span>
          <span className="labor-audit-emp-kpi-label">Actual</span>
        </div>
        <div className={`labor-audit-emp-kpi labor-audit-emp-kpi--delta ${deltaClass}`}>
          <span className="labor-audit-emp-kpi-value">{deltaSign}{formatHours(Math.abs(audit.delta))}<span className="u">h</span></span>
          <span className="labor-audit-emp-kpi-label">Δ actual–sched</span>
        </div>
        <div className="labor-audit-emp-kpi">
          <span className="labor-audit-emp-kpi-value">{audit.roleCount}</span>
          <span className="labor-audit-emp-kpi-label">Roles</span>
        </div>
      </div>

      {audit.flags.length > 0 && (
        <ul className="labor-audit-emp-flags" aria-label="Variance flags">
          {audit.flags.map((f, i) => (
            <li key={i} className={`labor-audit-emp-flag is-${f.kind}`}>
              <span className="labor-audit-emp-flag-tag">{f.kind === 'no_show' ? 'No-show' : f.kind === 'extra' ? 'Unscheduled' : 'Variance'}</span>
              <span className="labor-audit-emp-flag-text">{f.text}</span>
            </li>
          ))}
        </ul>
      )}

      {tab === 'week' && (
        <>
          <div className="labor-audit-emp-section-head">
            <span className="labor-audit-emp-eyebrow">Daily breakdown</span>
            <span className="labor-audit-emp-section-sub">Scheduled vs actual</span>
          </div>
          <ol className="labor-audit-emp-days">
            {audit.dayRows.map((d, i) => {
              const schedPct = (d.sched / audit.dayMax) * 100;
              const actualPct = (d.actual / audit.dayMax) * 100;
              return (
                <li key={i} className={`labor-audit-emp-day ${d.isToday ? 'is-today' : ''}`}>
                  <div className="labor-audit-emp-day-head">
                    <span className="labor-audit-emp-day-label">
                      {d.date.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })}
                    </span>
                    <span className="labor-audit-emp-day-numbers">
                      <span title="Actual">{d.actual === 0 ? '—' : `${formatHours(d.actual)}h`}</span>
                      <span className="labor-audit-emp-day-sep">/</span>
                      <span title="Scheduled" className="is-sched">{d.sched === 0 ? '—' : `${formatHours(d.sched)}h`}</span>
                    </span>
                  </div>
                  <div className="labor-audit-emp-day-bars" aria-hidden="true">
                    <span className="labor-audit-emp-day-bar">
                      <span className="labor-audit-emp-day-bar-fill is-sched" style={{ width: `${schedPct}%` }} />
                    </span>
                    <span className="labor-audit-emp-day-bar">
                      <span className="labor-audit-emp-day-bar-fill is-actual" style={{ width: `${actualPct}%` }} />
                    </span>
                  </div>
                </li>
              );
            })}
          </ol>
        </>
      )}

      <div className="labor-audit-emp-section-head">
        <span className="labor-audit-emp-eyebrow">Shifts & clock-ins</span>
        <span className="labor-audit-emp-section-sub">
          {audit.entries.length} {audit.entries.length === 1 ? 'entry' : 'entries'}
        </span>
      </div>

      {audit.entries.length === 0 ? (
        <div className="labor-audit-drawer-empty">No scheduled shifts or time entries for this period.</div>
      ) : (
        <ul className="labor-audit-emp-entries">
          {audit.entries.map((e, i) => (
            <li key={i} className="labor-audit-emp-entry">
              <span className={`labor-audit-emp-entry-badge is-${e.source}`}>
                {e.source === 'actual' ? 'A' : 'S'}
              </span>
              <div className="labor-audit-emp-entry-body">
                <div className="labor-audit-emp-entry-head">
                  <span className="labor-audit-emp-entry-role">{e.roleName}</span>
                  <span className="labor-audit-emp-entry-date">
                    {e.start.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })}
                  </span>
                </div>
                <div className="labor-audit-emp-entry-times">
                  <span>{formatTime(e.start.getTime())} – {e.active ? <em>now</em> : formatTime(e.end.getTime())}</span>
                  <span className="labor-audit-emp-entry-hrs">
                    <strong>{formatHours(e.hours)}</strong><span className="u">h</span>
                  </span>
                </div>
              </div>
            </li>
          ))}
        </ul>
      )}
    </>
  );
};

/**
 * Aggregate ONE employee's labor data inside a window. Returns:
 *   - schedHours, actualHours, delta
 *   - dayRows[7] (week tab only) — per-day sched + actual
 *   - entries — chronological merged list of shifts and time entries
 *   - flags — anomalies worth surfacing (scheduled-no-show, unscheduled-clock-in)
 *   - roleCount, dayMax
 */
function buildEmployeeAudit({
  empGuid, shifts, timeEntries, periodStart, periodEnd, tab, getRoleName,
}) {
  const startMs = periodStart.getTime();
  const endMs = periodEnd.getTime();
  const nowMs = Date.now();
  const dayMs = 24 * 60 * 60 * 1000;
  const isWeek = tab === 'week';

  // Filter to this employee + period window.
  const filterEntries = (list, isActual) => {
    const out = [];
    for (const e of list || []) {
      if (e.employeeReference?.guid !== empGuid) continue;
      if (!e.inDate) continue;
      const s = new Date(e.inDate).getTime();
      const en = e.outDate ? new Date(e.outDate).getTime() : nowMs;
      if (!Number.isFinite(s) || !Number.isFinite(en) || en <= s) continue;
      if (en <= startMs || s >= endMs) continue;
      const hrs = overlapMs(s, en, startMs, endMs) / (1000 * 60 * 60);
      const jobGuid = e.jobReference?.guid || '__none__';
      out.push({
        source: isActual ? 'actual' : 'sched',
        start: new Date(Math.max(s, startMs)),
        end: new Date(Math.min(en, endMs)),
        rawStart: s,
        rawEnd: en,
        hours: hrs,
        roleGuid: jobGuid,
        roleName: getRoleName(jobGuid),
        active: isActual && !e.outDate && nowMs < endMs,
      });
    }
    return out;
  };

  const schedEntries = filterEntries(shifts, false);
  const actualEntries = filterEntries(timeEntries, true);
  const allEntries = [...schedEntries, ...actualEntries]
    .sort((a, b) => a.rawStart - b.rawStart);

  const schedHours = schedEntries.reduce((a, e) => a + e.hours, 0);
  const actualHours = actualEntries.reduce((a, e) => a + e.hours, 0);
  const delta = actualHours - schedHours;

  const roleSet = new Set();
  for (const e of allEntries) roleSet.add(e.roleGuid);

  // Daily breakdown for week tab.
  const dayRows = [];
  if (isWeek) {
    for (let d = 0; d < 7; d++) {
      const dStart = startMs + d * dayMs;
      const dEnd = dStart + dayMs;
      const sched = schedEntries.reduce((acc, e) => acc + overlapMs(e.rawStart, e.rawEnd, dStart, dEnd) / (1000 * 60 * 60), 0);
      const actual = actualEntries.reduce((acc, e) => acc + overlapMs(e.rawStart, e.rawEnd, dStart, dEnd) / (1000 * 60 * 60), 0);
      const date = new Date(dStart);
      const today = startOfDay(new Date());
      dayRows.push({
        date,
        sched,
        actual,
        isToday: isSameDay(date, today),
      });
    }
  }

  const dayMax = Math.max(1, ...dayRows.flatMap(r => [r.sched, r.actual]));

  // Variance flags — scoped narrowly so the panel only shows them when
  // useful. No-show: scheduled hrs > 0 and actual hrs === 0 for the same
  // day. Unscheduled: actual hrs > 0 and scheduled === 0 for the same day.
  const flags = [];
  if (isWeek) {
    for (const r of dayRows) {
      if (r.sched > 0.05 && r.actual === 0 && r.date.getTime() < nowMs) {
        flags.push({
          kind: 'no_show',
          text: `${r.date.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })} — scheduled ${formatHours(r.sched)}h, didn't clock in`,
        });
      } else if (r.actual > 0.05 && r.sched === 0) {
        flags.push({
          kind: 'extra',
          text: `${r.date.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })} — clocked ${formatHours(r.actual)}h, no scheduled shift`,
        });
      }
    }
  } else {
    if (schedHours > 0.05 && actualHours === 0 && periodEnd.getTime() < nowMs) {
      flags.push({ kind: 'no_show', text: `Scheduled ${formatHours(schedHours)}h but didn't clock in` });
    } else if (actualHours > 0.05 && schedHours === 0) {
      flags.push({ kind: 'extra', text: `Clocked ${formatHours(actualHours)}h without a scheduled shift` });
    }
  }

  return {
    schedHours,
    actualHours,
    delta,
    roleCount: roleSet.size,
    dayRows,
    dayMax,
    entries: allEntries,
    flags,
  };
}

// ─── Helpers ────────────────────────────────────────────────────────

/**
 * Build a window-scoped roster selection from a swimlane slot click.
 *
 * `s.roleGuid === null` means the click was on the "ALL" coverage row —
 * we pool rosters across every role in the agg. Otherwise we scope to
 * the clicked role. Roster entries are filtered to those overlapping
 * the half-hour window, with `hours` re-computed as the overlap so the
 * drawer's per-person hours reflect *time inside this slot*, not the
 * whole shift.
 */
function slotSelection(s, agg, source, selectedDay) {
  const slotMs = (24 * 60 * 60 * 1000) / HOUR_BINS;
  const dayStartMs = startOfDay(selectedDay).getTime();
  const windowStart = dayStartMs + s.slot * slotMs;
  const windowEnd = windowStart + slotMs;
  const inWindow = (entry) =>
    Number.isFinite(entry.start) && Number.isFinite(entry.end)
      && entry.end > windowStart && entry.start < windowEnd;

  // Keep each entry's *real* start/end and full-shift hours so the
  // drawer shows actual shift context (e.g. "12:00 PM – 10:00 PM, 10h")
  // for someone who is on the clock during the clicked half-hour. The
  // filter is just "did this shift overlap the window?".
  let roster = [];
  let roleName, roleColor, roleGuid;
  if (s.roleGuid == null) {
    for (const r of agg.roles) {
      for (const e of r.roster) {
        if (inWindow(e)) {
          roster.push({ ...e, roleName: r.name, roleColor: r.color });
        }
      }
    }
    roleName = 'All roles';
    roleColor = '#1a1a1a';
    roleGuid = null;
  } else {
    const role = agg.roles.find(r => r.guid === s.roleGuid);
    if (role) {
      for (const e of role.roster) {
        if (inWindow(e)) roster.push({ ...e });
      }
      roleName = role.name;
      roleColor = role.color;
      roleGuid = role.guid;
    }
  }

  return {
    source,
    roleGuid,
    roleName,
    roleColor,
    roster,
    windowStart,
    windowEnd,
    slotIndex: s.slot,
  };
}

function deltaLabel(actual, sched) {
  if (sched === 0 && actual === 0) return '—';
  const diff = actual - sched;
  if (Math.abs(diff) < 0.05) return '0';
  const sign = diff > 0 ? '+' : '−';
  return `${sign}${formatHours(Math.abs(diff))}`;
}

function deltaSub(actual, sched) {
  if (sched === 0) return null;
  const diff = actual - sched;
  const pct = (diff / sched) * 100;
  if (Math.abs(pct) < 0.5) return '±0%';
  const sign = pct > 0 ? '+' : '';
  return `${sign}${Math.round(pct)}%`;
}

export default ToastLaborAuditView;
